import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Footprints, Loader2, RotateCw, Users } from "lucide-react";
import { api } from "../lib/api";

const TABS = [
  { id: "all", label: "Tous", match: null },
  { id: "rythme", label: "Rythme", match: "rythme" },
  { id: "fps", label: "FPS", match: "fps" },
  { id: "horreur", label: "Horreur", match: "horreur" },
  { id: "simulation", label: "Simulation", match: "simulation" },
  { id: "coop", label: "Co-op", match: "co-op" },
];

export const GameCatalog = ({ onReserve }) => {
  const [experiences, setExperiences] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/experiences")
      .then((r) => setExperiences(r.data))
      .catch(() => setExperiences([]))
      .finally(() => setLoading(false));
  }, []);

  const active = TABS.find((t) => t.id === tab);
  const filtered =
    tab === "all"
      ? experiences
      : experiences.filter((e) => e.category.toLowerCase().includes(active.match));

  return (
    <section id="experiences" data-testid="experiences-section" className="relative py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-hud text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 01 — Expériences"}
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Choisissez votre <span className="text-glow-cyan text-cyan-400">monde</span>
          </h2>
        </motion.div>

        <div data-testid="catalog-tabs" className="mt-10 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`catalog-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`border px-5 py-2 font-hud text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                tab === t.id
                  ? "border-cyan-400 bg-cyan-400/10 text-cyan-300 shadow-[0_0_18px_rgba(0,240,255,0.2)]"
                  : "border-line text-slate-400 hover:border-cyan-500/40 hover:text-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : (
          <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((exp) => (
                <motion.article
                  key={exp.slug}
                  layout
                  data-testid={`game-card-${exp.slug}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45 }}
                  className="group relative overflow-hidden border border-line bg-surface transition-colors duration-300 hover:border-cyan-400/50"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={exp.image}
                      alt={exp.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/20 to-transparent" />
                    <span className="absolute left-3 top-3 bg-void/80 px-2.5 py-1 font-hud text-[10px] uppercase tracking-[0.2em] text-cyan-300 backdrop-blur-sm">
                      {exp.category}
                    </span>
                    <span className="absolute right-3 top-3 flex items-center gap-1.5 bg-void/80 px-2.5 py-1 font-hud text-[10px] uppercase tracking-[0.2em] text-fuchsia-300 backdrop-blur-sm">
                      {exp.machine === "katvr" ? (
                        <Footprints className="h-3 w-3" />
                      ) : (
                        <RotateCw className="h-3 w-3" />
                      )}
                      {exp.machine === "katvr" ? "KAT VR" : "360°"}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-slate-100">
                        {exp.title}
                      </h3>
                      <span className="flex shrink-0 items-center gap-1.5 font-hud text-[10px] uppercase tracking-widest text-slate-500">
                        <Users className="h-3.5 w-3.5" />
                        {exp.players}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-400">
                      {exp.description}
                    </p>
                    <div className="mt-5 flex items-center justify-between border-t border-line pt-4">
                      <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-amber-400">
                        {exp.difficulty} · {exp.duration} min
                      </span>
                      <button
                        data-testid={`game-reserve-${exp.slug}`}
                        onClick={() => onReserve(exp)}
                        className="font-hud text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 transition-colors duration-300 hover:text-cyan-200"
                      >
                        Réserver →
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
